import { useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Breadcrumb, Typography } from "antd";

import { getClaimById } from "../../pages/claim/api/claimAPI";
import { getFileById } from "../../pages/files/api/fileAPI";
import { getLeaveById } from "../../pages/leave/api/leaveAPI";
import { getPayslipById } from "../../pages/payslip/api/payslipAPI";
import { getUserById } from "../../pages/user/api/userAPI";
import { getUser } from "../../services/auth";

const { Text } = Typography;

// Define allowed routes for each role
const allowedRoutes = {
  admin: [
    "/dashboard",
    "/users",
    "/users/new",
    "/users/:id",
    "/files",
    "/files/new",
    "/files/:id",
    "/files/:id/edit",
    "/files/:id/status",
    "/leaves",
    "/leaves/apply",
    "/leaves/:id/edit",
    "/claims",
    "/claims/new",
    "/claims/:id/edit",
    "/payslips",
    "/payslips/new",
    "/payslips/:id/edit",
  ],
  branchAdmin: [
    "/dashboard",
    "/users/:id",
    "/files",
    "/files/new",
    "/files/:id",
    "/files/:id/edit",
    "/files/:id/status",
    "/leaves",
    "/leaves/apply",
    "/leaves/:id/edit",
    "/claims",
    "/claims/new",
    "/claims/:id/edit",
    "/payslips",
    "/payslips/new",
    "/payslips/:id/edit",
  ],
  lawyer: [
    "/dashboard",
    "/users/:id",
    "/files",
    "/files/new",
    "/files/:id",
    "/files/:id/edit",
    "/files/:id/status",
    "/leaves",
    "/leaves/apply",
    "/leaves/:id/edit",
    "/claims",
    "/claims/new",
    "/claims/:id/edit",
    "/payslips",
  ],
  staff: [
    "/dashboard",
    "/users/:id",
    "/files",
    "/files/new",
    "/files/:id",
    "/files/:id/edit",
    "/files/:id/status",
    "/leaves",
    "/leaves/apply",
    "/leaves/:id/edit",
    "/claims",
    "/claims/new",
    "/claims/:id/edit",
    "/payslips",
  ],
  client: [
    "/dashboard",
    "/users/:id",
    "/files",
    "/files/:id",
    "/files/:id/status",
  ],
  agent: [
    "/dashboard",
    "/users/:id",
    "/files",
    "/files/:id",
    "/files/:id/status",
  ],
};

const DynamicBreadcrumb: React.FC = () => {
  const location = useLocation();
  const user = getUser();
  const userRole = user.role as keyof typeof allowedRoutes;

  // Extract file ID and user ID from the route path
  const routes = location.pathname.split("/").filter(Boolean);
  const fileIdIndex = routes.findIndex(
    (r, i) =>
      i > 0 &&
      routes[i - 1] === "files" &&
      !["new", "edit", "status"].includes(r)
  );
  const userIdIndex = routes.findIndex(
    (r, i) =>
      i > 0 &&
      routes[i - 1] === "users" &&
      !["new", "edit", "status"].includes(r)
  );
  const leaveIdIndex = routes.findIndex(
    (r, i) =>
      i > 0 && routes[i - 1] === "leaves" && !["apply", "edit"].includes(r)
  );
  const claimIdIndex = routes.findIndex(
    (r, i) =>
      i > 0 && routes[i - 1] === "claims" && !["new", "edit"].includes(r)
  );
  const payslipIdIndex = routes.findIndex(
    (r, i) =>
      i > 0 && routes[i - 1] === "payslips" && !["new", "edit"].includes(r)
  );

  const fileId = fileIdIndex !== -1 ? routes[fileIdIndex] : undefined;
  const userId = userIdIndex !== -1 ? routes[userIdIndex] : undefined;
  const leaveId = leaveIdIndex !== -1 ? routes[leaveIdIndex] : undefined;
  const claimId = claimIdIndex !== -1 ? routes[claimIdIndex] : undefined;
  const payslipId = payslipIdIndex !== -1 ? routes[payslipIdIndex] : undefined;

  // Fetch file data if we have a file ID
  const { data: fileData } = useQuery({
    queryKey: ["file", fileId],
    queryFn: () => getFileById(fileId),
    enabled: !!fileId,
  });

  // Fetch user data if we have a user ID
  const { data: userData } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
  });

  // Fetch leave data if we have a leave ID
  const { data: leaveData } = useQuery({
    queryKey: ["leave", leaveId],
    queryFn: () => getLeaveById(leaveId),
    enabled: !!leaveId,
  });

  // Fetch claim data if we have a claim ID
  const { data: claimData } = useQuery({
    queryKey: ["claim", claimId],
    queryFn: () => getClaimById(claimId),
    enabled: !!claimId,
  });

  // Fetch payslip data if we have a payslip ID
  const { data: payslipData } = useQuery({
    queryKey: ["payslip", payslipId],
    queryFn: () => getPayslipById(payslipId),
    enabled: !!payslipId,
  });

  const isRouteAllowed = useCallback(
    (path: string) => {
      const userAllowedRoutes = allowedRoutes[userRole] || [];
      // Convert dynamic route parameters to match the allowed routes pattern
      const normalizedPath = path.replace(/\/[0-9a-fA-F-]+(?=\/|$)/g, "/:id");
      return userAllowedRoutes.some((route) => {
        // Convert route pattern to regex to match dynamic segments
        const routePattern = new RegExp(
          "^" + route.replace(/:id/g, "[^/]+") + "$"
        );
        return routePattern.test(normalizedPath);
      });
    },
    [userRole]
  );

  const breadcrumbs = useMemo(() => {
    const routeSegments = location.pathname.split("/").filter(Boolean);
    return routeSegments.map((route, index) => {
      const path = `/${routeSegments.slice(0, index + 1).join("/")}`;
      let title = route;

      // Check if we're on a route with an ID
      const prevRoute = routeSegments[index - 1];
      const isSpecialRoute = ["new", "edit", "status"].includes(route);
      const isRouteClickable = isRouteAllowed(path);

      if (prevRoute === "files" && !isSpecialRoute) {
        // This is a file ID route - show refNumber if available
        // Only show refNumber if this route matches the fileId we're fetching
        const isCurrentFileId = route === fileId;
        const content = (isCurrentFileId && fileData?.refNumber) || route;
        const isLastItem = index === routeSegments.length - 1;

        return {
          title:
            !isLastItem && isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {content}
              </Link>
            ) : (
              <Text
                style={{
                  color: isLastItem ? "#0C377C" : "#999",
                  fontWeight: isLastItem ? "bold" : "normal",
                  textTransform: "uppercase",
                }}
              >
                {content}
              </Text>
            ),
        };
      }

      if (prevRoute === "users" && !isSpecialRoute) {
        // This is a user ID route - show name if available
        // Only show name if this route matches the userId we're fetching
        const isCurrentUserId = route === userId;
        const content = (isCurrentUserId && userData?.name) || route;
        const isLastItem = index === routeSegments.length - 1;

        return {
          title:
            !isLastItem && isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {content}
              </Link>
            ) : (
              <Text
                style={{
                  color: isLastItem ? "#0C377C" : "#999",
                  fontWeight: isLastItem ? "bold" : "normal",
                  textTransform: "uppercase",
                }}
              >
                {content}
              </Text>
            ),
        };
      }

      if (prevRoute === "leaves" && !isSpecialRoute) {
        // This is a leave ID route - show leave type if available
        // Only show leave type if this route matches the leaveId we're fetching
        const isCurrentLeaveId = route === leaveId;
        const content = (isCurrentLeaveId && leaveData?.user?.name) || route;
        const isLastItem = index === routeSegments.length - 1;

        return {
          title:
            !isLastItem && isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {content}
              </Link>
            ) : (
              <Text
                style={{
                  color: isLastItem ? "#0C377C" : "#999",
                  fontWeight: isLastItem ? "bold" : "normal",
                  textTransform: "uppercase",
                }}
              >
                {content}
              </Text>
            ),
        };
      }

      if (prevRoute === "claims" && !isSpecialRoute) {
        // This is a claim ID route - show claim type if available
        // Only show claim type if this route matches the claimId we're fetching
        const isCurrentClaimId = route === claimId;
        const content = (isCurrentClaimId && claimData?.claimType) || route;
        const isLastItem = index === routeSegments.length - 1;

        return {
          title:
            !isLastItem && isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {content}
              </Link>
            ) : (
              <Text
                style={{
                  color: isLastItem ? "#0C377C" : "#999",
                  fontWeight: isLastItem ? "bold" : "normal",
                  textTransform: "uppercase",
                }}
              >
                {content}
              </Text>
            ),
        };
      }

      if (prevRoute === "payslips" && !isSpecialRoute) {
        // This is a payslip ID route - show payslip date if available
        // Only show payslip date if this route matches the payslipId we're fetching
        const isCurrentPayslipId = route === payslipId;
        const content =
          (isCurrentPayslipId && payslipData?.user?.name) || route;
        const isLastItem = index === routeSegments.length - 1;

        return {
          title:
            !isLastItem && isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {content}
              </Link>
            ) : (
              <Text
                style={{
                  color: isLastItem ? "#0C377C" : "#999",
                  fontWeight: isLastItem ? "bold" : "normal",
                  textTransform: "uppercase",
                }}
              >
                {content}
              </Text>
            ),
        };
      }

      if (!isSpecialRoute) {
        title = title.charAt(0).toUpperCase() + title.slice(1);
      }

      return {
        title:
          index < routeSegments.length - 1 ? (
            isRouteClickable ? (
              <Link to={path} style={{ textTransform: "uppercase" }}>
                {title}
              </Link>
            ) : (
              <Text style={{ textTransform: "uppercase", color: "#999" }}>
                {title}
              </Text>
            )
          ) : (
            <Text
              style={{
                color: "#0C377C",
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            >
              {title}
            </Text>
          ),
      };
    });
  }, [
    location.pathname,
    isRouteAllowed,
    fileData,
    userData,
    fileId,
    userId,
    leaveData,
    leaveId,
    claimData,
    claimId,
    payslipData,
    payslipId,
  ]);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", overflowX: "auto" }}>
      <Breadcrumb items={breadcrumbs} separator=">" />
    </div>
  );
};

export default DynamicBreadcrumb;
